// Tiny fetch wrapper: holds the access token in-memory, sends it as a Bearer
// header, and on 401 transparently retries once after hitting /auth/refresh
// (refresh token lives in an HttpOnly cookie set by the server).

const BASE = '/api/v1';
let accessToken = null;
let refreshing = null;

async function tryRefresh() {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const r = await fetch(BASE + '/auth/refresh', { method: 'POST', credentials: 'include' });
      if (r.status === 204 || !r.ok) return false; // 204 = no session yet (cold load)
      const j = await r.json();
      accessToken = j.accessToken;
      return true;
    } catch { return false; }
    finally { refreshing = null; }
  })();
  return refreshing;
}

async function request(method, path, body, retried = false) {
  const headers = {};
  if (body != null) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const res = await fetch(BASE + path, {
    method, headers, credentials: 'include',
    body: body != null ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 && !retried && accessToken) {
    if (await tryRefresh()) return request(method, path, body, true);
    accessToken = null;
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    let msg = res.statusText;
    try { const j = await res.json(); if (j && j.error) msg = j.error; } catch {}
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  const ct = res.headers.get('Content-Type') || '';
  if (ct.includes('json')) return res.json();
  return res.text();
}

const api = {
  setToken(t) { accessToken = t; },
  getToken() { return accessToken; },
  clear() { accessToken = null; },
  tryRefresh,
  get(p) { return request('GET', p); },
  post(p, b) { return request('POST', p, b); },
  put(p, b) { return request('PUT', p, b); },
  delete(p) { return request('DELETE', p); },
};

// Multipart form helper used by the Import wizard. Uses the same auth + 401
// retry as JSON requests, but doesn't set Content-Type (the browser sets the
// multipart boundary automatically when the body is a FormData instance).
api.postForm = async function (path, formData, retried = false) {
  const headers = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const res = await fetch(BASE + path, { method: 'POST', headers, credentials: 'include', body: formData });
  if (res.status === 401 && !retried && accessToken) {
    if (await tryRefresh()) return api.postForm(path, formData, true);
    accessToken = null; throw new Error('Unauthorized');
  }
  if (!res.ok) {
    let msg = res.statusText;
    try { const j = await res.json(); if (j && j.error) msg = j.error; } catch {}
    const err = new Error(msg); err.status = res.status; throw err;
  }
  if (res.status === 204) return null;
  return res.json();
};

// POST a multipart form and stream the binary response back as a download
// (e.g. a generated .xlsx). Same auth + 401-retry semantics as postForm.
api.postFormDownload = async function (path, formData, filename, retried = false) {
  const headers = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const res = await fetch(BASE + path, { method: 'POST', headers, credentials: 'include', body: formData });
  if (res.status === 401 && !retried && accessToken) {
    if (await tryRefresh()) return api.postFormDownload(path, formData, filename, true);
    accessToken = null; throw new Error('Unauthorized');
  }
  if (!res.ok) {
    let msg = res.statusText;
    try { const j = await res.json(); if (j && j.error) msg = j.error; } catch {}
    const err = new Error(msg); err.status = res.status; throw err;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

window.QH_API = api;

// Streams a file response back as a download. The bearer token is sent via
// a transient form/blob round-trip since native <a download> can't set
// custom headers — we fetch with auth, then create an object URL. Handles
// the same 401-then-refresh path as the JSON request helper, so an expired
// access token doesn't silently break exports the user clicks.
window.downloadCsv = async function (path, filename, retried = false) {
  const headers = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  let res;
  try { res = await fetch(BASE + path, { headers, credentials: 'include' }); }
  catch (e) { throw new Error('Network error: ' + e.message); }
  if (res.status === 401 && !retried) {
    if (await tryRefresh()) return window.downloadCsv(path, filename, true);
    throw new Error('Session expired — please sign in again.');
  }
  if (!res.ok) {
    let msg = `Export failed (HTTP ${res.status})`;
    try { const j = await res.json(); if (j && j.error) msg = j.error; } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

// Convenience wrapper: invoke from a button onClick. Surfaces failures via
// alert() instead of letting unhandled promise rejections swallow them.
window.safeDownload = async function (path, filename) {
  try { await window.downloadCsv(path, filename); }
  catch (e) { alert(e.message || 'Download failed.'); }
};
